// src/components/Common/Sidebar.js

import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../App';
import { 
    FaTachometerAlt, FaBuilding, FaBullhorn, FaFileInvoice, FaMoneyBillWave, 
    FaCreditCard, FaClipboardList, FaHistory, FaClock, FaCamera, FaCalendarAlt, 
    FaUsersCog, FaCog, FaUserPlus, FaDollarSign, FaChartLine, FaBroadcastTower, 
    FaUserEdit, FaChevronRight, FaSignOutAlt, FaNewspaper, FaLock
} from 'react-icons/fa';

const parseFrequencyNumber = (frequency) => {
    if (!frequency) return null;
    const match = String(frequency).match(/(\d+(\.\d+)?)/);
    if (!match) return null;
    const n = Number(match[1]);
    return Number.isFinite(n) ? n : null;
};

const getRadioStations = (allStations) => {
    return (allStations || []).filter(s => {
        const frequency = (s.frequency || '').trim().toUpperCase();
        const city = (s.city || '').trim().toUpperCase();
        const name = (s.name || '').toLowerCase();
        return Boolean(s.id) && frequency && frequency !== 'N/A' && city !== 'N/A' && !name.includes('flare');
    });
};

const findFlareBusiness = (allStations) => {
    return (allStations || []).find(s => {
        const name = (s.name || '').toLowerCase();
        const frequency = (s.frequency || '').trim().toUpperCase();
        return name.includes('flare') || frequency === 'N/A';
    }) || null;
};

const getStationTabLabel = (station, fallback) => {
    if (!station) return fallback;
    const freq = parseFrequencyNumber(station.frequency);
    if (Number.isFinite(freq)) {
        return `Mix FM ${Math.round(freq)}`;
    }
    return station.name || fallback;
};

const BusinessSwitcher = ({ allStations, currentProject, currentStationId, onSelect }) => {
    const radioStations = useMemo(() => {
        const stations = getRadioStations(allStations).map(s => ({ ...s, _freq: parseFrequencyNumber(s.frequency) ?? 99999 }));
        const station99 = stations.find(s => String(s.frequency).includes('99') || (s._freq >= 98.5 && s._freq < 99.6));
        const station100 = stations.find(s => String(s.frequency).includes('100') || (s._freq >= 99.5 && s._freq < 100.6));
        const picked = [];
        if (station99) picked.push(station99);
        if (station100 && station100.id !== station99?.id) picked.push(station100);
        if (picked.length < 2) {
            const remaining = stations.filter(s => !picked.some(p => p.id === s.id)).sort((a, b) => (a._freq ?? 99999) - (b._freq ?? 99999));
            picked.push(...remaining.slice(0, 2 - picked.length));
        }
        return picked.slice(0, 2);
    }, [allStations]);

    const flareBusiness = useMemo(() => findFlareBusiness(allStations), [allStations]);

    const activeKey = useMemo(() => {
        if (currentProject === 'FLARE') return 'FLARE';
        return currentStationId || null;
    }, [currentProject, currentStationId]);

    return (
        <div className="px-6 mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">Active Project:</label>
            <div className="grid grid-cols-3 gap-2">
                <button
                    onClick={() => radioStations[0] && onSelect({ project: 'MIXFM', stationId: radioStations[0].id })}
                    disabled={!radioStations[0]}
                    className={`py-2 text-[11px] font-bold rounded-md transition-all ${activeKey === radioStations[0]?.id ? 'bg-rose-600 text-white shadow-lg' : 'bg-slate-700 text-slate-200 hover:text-white'} disabled:opacity-50`}
                >
                    {getStationTabLabel(radioStations[0], 'Mix FM 1')}
                </button>
                <button
                    onClick={() => radioStations[1] && onSelect({ project: 'MIXFM', stationId: radioStations[1].id })}
                    disabled={!radioStations[1]}
                    className={`py-2 text-[11px] font-bold rounded-md transition-all ${activeKey === radioStations[1]?.id ? 'bg-rose-600 text-white shadow-lg' : 'bg-slate-700 text-slate-200 hover:text-white'} disabled:opacity-50`}
                >
                    {getStationTabLabel(radioStations[1], 'Mix FM 2')}
                </button>
                <button
                    onClick={() => flareBusiness && onSelect({ project: 'FLARE', stationId: flareBusiness.id })}
                    disabled={!flareBusiness}
                    className={`py-2 text-[11px] font-bold rounded-md transition-all ${activeKey === 'FLARE' ? 'bg-rose-600 text-white shadow-lg' : 'bg-slate-700 text-slate-200 hover:text-white'} disabled:opacity-50`}
                >
                    Flare
                </button>
            </div>
        </div>
    );
};


// --- Reusable UI Components (No changes here) ---

const SidebarLink = ({ text, icon, tabName, activeTab, onClick }) => (
    <div className="px-4">
        <Link
            to={`/${tabName}`}
            onClick={onClick}
            className={`w-full text-left px-4 py-3 flex items-center space-x-4 rounded-lg transition-all duration-200 ${
                activeTab === tabName 
                ? 'bg-rose-600 text-white shadow-lg'
                : 'text-slate-200 hover:bg-slate-700 hover:text-white'
            }`}
        >
            <span className="text-xl">{icon}</span>
            <span className="font-medium">{text}</span>
        </Link>
    </div>
);

const DropdownMenu = ({ title, icon, items, activeTab, onClick, hasAccess }) => {
    const isActive = useMemo(() => items.some(item => item.tabName === activeTab), [items, activeTab]);
    const [isOpen, setIsOpen] = useState(isActive);
    const accessibleItems = items.filter(item => hasAccess(item.accessKey));
    if (accessibleItems.length === 0) return null;
    return (
        <div className="px-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full text-left px-4 py-3 flex items-center space-x-4 rounded-lg transition-all duration-200 ${
                    isOpen || isActive ? 'bg-slate-700 text-white' : 'text-slate-200 hover:bg-slate-700 hover:text-white'
                }`}
            >
                <span className="text-xl">{icon}</span>
                <span className="font-medium">{title}</span>
                <FaChevronRight className={`ml-auto h-4 w-4 transform transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
            </button>
            {isOpen && (
                <div className="ml-5 mt-2 space-y-1 border-l-2 border-slate-600 pl-6 py-1">
                    {accessibleItems.map(item => (
                        <Link
                            key={item.tabName}
                            to={`/${item.tabName}`}
                            onClick={onClick}
                            className={`w-full text-left px-3 py-2 flex items-center space-x-3 rounded-md text-sm transition-colors duration-200 ${
                                activeTab === item.tabName 
                                ? 'bg-rose-500 text-white'
                                : 'text-slate-300 hover:bg-slate-600 hover:text-white'
                            }`}
                        >
                            {item.icon && <span className="text-lg">{item.icon}</span>}
                            <span>{item.text}</span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- Sidebar Configuration ---
const getSidebarConfig = (project) => {
    if (project === 'FLARE') {
        return [
            { type: 'link', tabName: 'dashboard', text: 'Dashboard', icon: <FaTachometerAlt />, accessKey: 'dashboard' },
            { type: 'link', tabName: 'magazineSales', text: 'Magazine Sales', icon: <FaNewspaper />, accessKey: 'magazineSales' },
            { type: 'link', tabName: 'clients', text: 'Clients', icon: <FaBuilding />, accessKey: 'clients' },
            { type: 'link', tabName: 'invoices', text: 'Invoices', icon: <FaFileInvoice />, accessKey: 'invoices' },
            { type: 'link', tabName: 'payments', text: 'Payments', icon: <FaMoneyBillWave />, accessKey: 'payments' },
            { type: 'link', tabName: 'expenses', text: 'Expenses', icon: <FaCreditCard />, accessKey: 'expenses' },
            { type: 'link', tabName: 'billManagement', text: 'Bill Management', icon: <FaClipboardList />, accessKey: 'billManagement' },
            { 
                type: 'dropdown', title: 'Attendance', icon: <FaClock />,
                items: [
                    { tabName: 'liveKiosk', text: 'Live Kiosk', icon: <FaCamera />, accessKey: 'liveKiosk' },
                    { tabName: 'monthlyReport', text: 'Monthly Report', icon: <FaCalendarAlt />, accessKey: 'monthlyReport' },
                ]
            },
            { 
                type: 'dropdown', title: 'HR Management', icon: <FaUsersCog />,
                items: [
                    { tabName: 'employeeRegistration', text: 'Employee Registration', icon: <FaUserPlus />, accessKey: 'employeeRegistration' },
                    { tabName: 'salaryManagement', text: 'Salary Management', icon: <FaDollarSign />, accessKey: 'salaryManagement' },
                ]
            },
            { type: 'link', role: 'admin', tabName: 'profitAndLossReport', text: 'P&L Report', icon: <FaChartLine />, accessKey: 'profitAndLossReport' },
            { 
                type: 'dropdown', title: 'Settings', icon: <FaCog />, role: 'admin',
                items: [
                    { tabName: 'userManagement', text: 'User Management', icon: <FaUsersCog />, accessKey: 'userManagement' },
                    { tabName: 'radioStationManagement', text: 'Manage Stations', icon: <FaBroadcastTower />, accessKey: 'radioStationManagement' },
                    { tabName: 'adminProfileSettings', text: 'Admin Settings', icon: <FaUserEdit />, accessKey: 'adminProfileSettings' },
                    { tabName: 'monthlyClosing', text: 'Monthly Closing', icon: <FaLock />, accessKey: 'monthlyClosing' },
                ]
            },
        ];
    }

    // Default Mix FM Config
    return [
        { type: 'link', tabName: 'dashboard', text: 'Dashboard', icon: <FaTachometerAlt />, accessKey: 'dashboard' },
        { type: 'link', tabName: 'clients', text: 'Clients', icon: <FaBuilding />, accessKey: 'clients' },
        { type: 'link', tabName: 'adCampaigns', text: 'Ad Campaigns', icon: <FaBullhorn />, accessKey: 'adCampaigns' },
        { type: 'link', tabName: 'invoices', text: 'Invoices', icon: <FaFileInvoice />, accessKey: 'invoices' },
        { type: 'link', tabName: 'payments', text: 'Payment Received', icon: <FaMoneyBillWave />, accessKey: 'payments' },
        { type: 'link', tabName: 'expenses', text: 'Expenses', icon: <FaCreditCard />, accessKey: 'expenses' },
        { type: 'link', tabName: 'billManagement', text: 'Bill Management', icon: <FaClipboardList />, accessKey: 'billManagement' },
        { type: 'link', tabName: 'adLogs', text: 'Ad Logs', icon: <FaHistory />, accessKey: 'adLogs' },
        { 
            type: 'dropdown', title: 'Attendance', icon: <FaClock />,
            items: [
                { tabName: 'liveKiosk', text: 'Live Kiosk', icon: <FaCamera />, accessKey: 'liveKiosk' },
                { tabName: 'monthlyReport', text: 'Monthly Report', icon: <FaCalendarAlt />, accessKey: 'monthlyReport' },
            ]
        },
        { 
            type: 'dropdown', title: 'HR Management', icon: <FaUsersCog />,
            items: [
                { tabName: 'employeeRegistration', text: 'Employee Registration', icon: <FaUserPlus />, accessKey: 'employeeRegistration' },
                { tabName: 'salaryManagement', text: 'Salary Management', icon: <FaDollarSign />, accessKey: 'salaryManagement' },
            ]
        },
        { type: 'link', role: 'admin', tabName: 'profitAndLossReport', text: 'Profit & Loss Report', icon: <FaChartLine />, accessKey: 'profitAndLossReport' },
        { 
            type: 'dropdown', title: 'Settings', icon: <FaCog />, role: 'admin',
            items: [
                { tabName: 'userManagement', text: 'User Management', icon: <FaUsersCog />, accessKey: 'userManagement' },
                { tabName: 'radioStationManagement', text: 'Manage Stations', icon: <FaBroadcastTower />, accessKey: 'radioStationManagement' },
                { tabName: 'adminProfileSettings', text: 'Admin Settings', icon: <FaUserEdit />, accessKey: 'adminProfileSettings' },
                    { tabName: 'monthlyClosing', text: 'Monthly Closing', icon: <FaLock />, accessKey: 'monthlyClosing' },
            ]
        },
    ];
};

// --- Main Sidebar Component ---

function Sidebar({ isSidebarOpen, setIsSidebarOpen, hasAccess }) {
    const navigate = useNavigate();
    const { 
        user, mainAppLogo, appTagline, activeTab,
        allStations, currentStationId, setCurrentStationId,
        availableStationsForUser, logout,
        currentProject, setCurrentProject
    } = useAppContext();

    const sidebarConfig = useMemo(() => getSidebarConfig(currentProject), [currentProject]);

    const handleLinkClick = (tabName) => {
        if (window.innerWidth < 768) setIsSidebarOpen(false);
    };
    
    const handleBusinessChange = ({ project, stationId }) => {
        setCurrentProject(project);
        setCurrentStationId(stationId);
        navigate('/dashboard');
        setIsSidebarOpen(false);
    };

    return (
        <aside className={`fixed inset-y-0 left-0 w-64 bg-slate-800 text-white shadow-xl transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:relative transition-transform duration-300 ease-in-out z-30 flex flex-col`}>
            
            <div className="p-6 flex flex-col items-center border-b border-slate-700 relative">
                <button type="button" onClick={() => setIsSidebarOpen(false)} className="absolute top-4 right-4 text-slate-300 hover:text-white md:hidden">
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </button>
                <a href="/" onClick={(e) => { e.preventDefault(); window.location.reload(); }} className="cursor-pointer transition-opacity duration-200 hover:opacity-90">
                    <img src={mainAppLogo} alt="Network Logo" className="h-16 w-auto mb-2 rounded-lg" style={currentProject === 'FLARE' ? { filter: 'brightness(0) invert(1)' } : undefined} onError={(e) => { e.target.src="https://placehold.co/100x50/1e293b/94a3b8?text=Logo"; }}/>
                </a>
                <p className="text-sm text-center text-slate-400">{appTagline}</p>
            </div>

            {user.role === 'admin' && (
                <div className="mt-4">
                    <BusinessSwitcher allStations={allStations} currentProject={currentProject} currentStationId={currentStationId} onSelect={handleBusinessChange} />
                </div>
            )}

            <nav className="flex-grow overflow-y-auto space-y-2 py-4">
                {user.role === 'staff' && (
                    <div className="px-6 mb-4">
                        <p className="block text-sm font-medium text-slate-300 mb-1">Your Station:</p>
                        <p className="text-white text-lg font-semibold">{availableStationsForUser.find(s => s.id === currentStationId)?.name || 'N/A'}</p>
                    </div>
                )}

                {sidebarConfig.map((item, index) => {
                    if (item.role && item.role !== user.role) return null;
                    if (item.type === 'link' && !hasAccess(item.accessKey)) return null;
                    return (
                        <React.Fragment key={index}>
                            {item.type === 'link' && <SidebarLink {...item} activeTab={activeTab} onClick={handleLinkClick} />}
                            {item.type === 'dropdown' && <DropdownMenu {...item} activeTab={activeTab} onClick={handleLinkClick} hasAccess={hasAccess} />}
                        </React.Fragment>
                    );
                })}
            </nav>
            
            <div className="p-4 border-t border-slate-700 bg-slate-900/50">
                <div className="flex items-center gap-3 mb-4">
                    <div className="h-10 w-10 rounded-full bg-slate-800 border border-slate-600 flex items-center justify-center font-bold">
                        {(() => {
                            const name = (user?.name || '').trim();
                            if (!name) return (user?.email || 'U').slice(0, 1).toUpperCase();
                            const parts = name.split(' ').filter(Boolean);
                            if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                            return parts[0].slice(0, 2).toUpperCase();
                        })()}
                    </div>
                    <div className="min-w-0">
                        <div className="font-semibold text-white truncate text-sm">{user?.name || 'User'}</div>
                        <div className="text-xs text-slate-300 font-mono truncate">{user?.email}</div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{user?.role}</div>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => { navigate('/myProfile'); setIsSidebarOpen(false); }} className="w-full bg-slate-700 text-white py-2 px-3 rounded-lg hover:bg-slate-600 transition-colors flex items-center justify-center gap-2 font-bold text-xs">
                        <FaUserEdit />
                        <span>Edit</span>
                    </button>
                    <button type="button" onClick={() => { logout(); setIsSidebarOpen(false); }} className="w-full bg-rose-600 text-white py-2 px-3 rounded-lg hover:bg-rose-500 transition-colors flex items-center justify-center gap-2 font-bold text-xs">
                        <FaSignOutAlt />
                        <span>Logout</span>
                    </button>
                </div>
            </div>
        </aside>
    );
}

export default Sidebar;
